import { useCallback, useEffect, useRef, useState } from 'react'

type WorkerTimerParams = {
  showHoursDefault?: boolean
  onUnload?: boolean
}

function isSafari() {
  return typeof window !== 'undefined' && /^((?!chrome|android).)*safari/i.test(window.navigator.userAgent)
}

export const useWorkerTimer = ({ showHoursDefault = true, onUnload = true }: WorkerTimerParams) => {
  const simpleTimer = `
    let timer = 0;
    const formatTime = (timer) => {
      const getSeconds = \`0\${timer % 60}\`.slice(-2);
      const minutes = Math.floor(timer / 60);
      const getMinutes = \`0\${minutes % 60}\`.slice(-2);
      const hours = Math.floor(timer / 3600);
      const getHours = \`0\${hours}\`.slice(-2);
      return \`\${${showHoursDefault} ? getHours + ':' : ''}\${getMinutes}:\${getSeconds}\`;
    };

    setInterval(() => {
      timer++;
      postMessage(formatTime(timer));
    }, 1000);
  `

  const [timerValue, setTimerValue] = useState(`${showHoursDefault ? '00:' : ''}00:00`)
  const worker = useRef<Worker | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const timerRef = useRef(0)

  const formatTime = useCallback(
    (timer: number) => {
      const getSeconds = `0${timer % 60}`.slice(-2)
      const minutes = Math.floor(timer / 60)
      const getMinutes = `0${minutes % 60}`.slice(-2)
      const hours = Math.floor(timer / 3600)
      const getHours = `0${hours}`.slice(-2)
      return `${showHoursDefault ? getHours + ':' : ''}${getMinutes}:${getSeconds}`
    },
    [showHoursDefault],
  )

  const startTimer = useCallback(() => {
    if (isSafari() || typeof Worker === 'undefined') {
      if (!intervalRef.current) {
        intervalRef.current = setInterval(() => {
          timerRef.current++
          setTimerValue(formatTime(timerRef.current))
        }, 1000)
      }
    } else {
      if (!worker.current) {
        const blob = new Blob([simpleTimer], { type: 'application/javascript' })
        worker.current = new Worker(URL.createObjectURL(blob))
        worker.current.onmessage = (event) => {
          setTimerValue(event.data)
        }
      }
    }
  }, [formatTime, simpleTimer])

  const stopTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
      timerRef.current = 0
      setTimerValue(`${showHoursDefault ? '00:' : ''}00:00`)
    }
    if (worker.current) {
      worker.current.terminate()
      worker.current = null
      setTimerValue(`${showHoursDefault ? '00:' : ''}00:00`)
    }
  }, [showHoursDefault])

  useEffect(() => {
    if (onUnload) {
      window.addEventListener('beforeunload', stopTimer)
    }
    return () => {
      if (onUnload) {
        window.removeEventListener('beforeunload', stopTimer)
      }
      stopTimer()
    }
  }, [onUnload, stopTimer])

  return { timerValue, startTimer, stopTimer }
}
